import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BrandLogo from './BrandLogo';

const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAuthenticated, isAdmin, user, logout } = useAuth();

  // Efect pentru detectarea scroll-ului
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    setIsMobileMenuOpen(false);
  };

  return (
    <header 
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled 
          ? 'bg-white shadow-md py-2' 
          : 'bg-gradient-to-r from-blue-600 to-blue-500 py-4'
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="block">
              <BrandLogo
                className={`w-auto transition-all duration-300 ${
                  isScrolled ? 'h-11 md:h-12' : 'h-12 md:h-14'
                }`}
              />
            </Link>
          </div>

          {/* Meniu desktop */}
          <nav className="hidden md:flex items-center space-x-1">
            <Link 
              to="/" 
              className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-700 hover:bg-gray-100' 
                  : 'text-white hover:bg-blue-700'
              }`}
            >
              Acasă
            </Link>
            <Link 
              to="/books" 
              className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-700 hover:bg-gray-100' 
                  : 'text-white hover:bg-blue-700'
              }`}
            >
              Cărți
            </Link>
            <Link 
              to="/recommendations" 
              className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-700 hover:bg-gray-100' 
                  : 'text-white hover:bg-blue-700'
              }`}
            >
              Recomandări
            </Link>
            {isAuthenticated && (
              <Link 
                to="/favorites" 
                className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                  isScrolled 
                    ? 'text-gray-700 hover:bg-gray-100' 
                    : 'text-white hover:bg-blue-700'
                }`}
              >
                Favorite
              </Link>
            )}
            <Link
              to="/copyright"
              className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                isScrolled 
                  ? 'text-gray-700 hover:bg-gray-100' 
                  : 'text-white hover:bg-blue-700'
              }`}
            >
              Copyright
            </Link>
            
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link 
                    to="/admin/dashboard" 
                    className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-700 hover:bg-gray-100' 
                        : 'text-white hover:bg-blue-700'
                    }`}
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/profile"
                  className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-100' 
                      : 'text-white hover:bg-blue-700'
                  }`}
                >
                  Profilul meu
                </Link>
                <div className="relative ml-3">
                  <button 
                    onClick={handleLogout} 
                    className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                      isScrolled 
                        ? 'text-gray-700 hover:bg-gray-100' 
                        : 'text-white hover:bg-blue-700'
                    }`}
                  >
                    Deconectare
                  </button>
                </div>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'text-gray-700 hover:bg-gray-100' 
                      : 'text-white hover:bg-blue-700'
                  }`}
                >
                  Autentificare
                </Link>
                <Link 
                  to="/register" 
                  className={`px-4 py-2 rounded-md font-medium transition-all duration-300 ${
                    isScrolled 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-white text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  Înregistrare
                </Link>
              </>
            )}
          </nav>

          {/* Buton menu pentru mobil */}
          <button 
            className="md:hidden"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              className={`h-6 w-6 transition-colors duration-300 ${isScrolled ? 'text-blue-600' : 'text-white'}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>

        {/* Meniu mobil */}
        <div 
          className={`md:hidden transition-all duration-300 overflow-hidden ${
            isMobileMenuOpen ? 'max-h-96 opacity-100 mt-4' : 'max-h-0 opacity-0'
          }`}
        >
          <div className={`flex flex-col space-y-2 pb-4 ${isScrolled ? 'text-gray-700' : 'text-white'}`}>
            <Link 
              to="/" 
              className={`px-4 py-2 rounded-md font-medium ${
                isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Acasă
            </Link>
            <Link 
              to="/books" 
              className={`px-4 py-2 rounded-md font-medium ${
                isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Cărți
            </Link>
            <Link 
              to="/recommendations" 
              className={`px-4 py-2 rounded-md font-medium ${
                isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Recomandări
            </Link>
            {isAuthenticated && (
              <Link 
                to="/favorites" 
                className={`px-4 py-2 rounded-md font-medium ${
                  isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Favorite
              </Link>
            )}
            <Link
              to="/copyright"
              className={`px-4 py-2 rounded-md font-medium ${
                isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
              }`}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Copyright
            </Link>
            
            {isAuthenticated ? (
              <>
                {isAdmin && (
                  <Link 
                    to="/admin/dashboard" 
                    className={`px-4 py-2 rounded-md font-medium ${
                      isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Admin
                  </Link>
                )}
                <Link
                  to="/profile"
                  className={`px-4 py-2 rounded-md font-medium ${
                    isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Profilul meu
                </Link>
                <button
                  onClick={handleLogout}
                  className={`px-4 py-2 text-left rounded-md font-medium ${
                    isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
                  }`}
                >
                  Deconectare
                </button>
              </>
            ) : (
              <>
                <Link 
                  to="/login" 
                  className={`px-4 py-2 rounded-md font-medium ${
                    isScrolled ? 'hover:bg-gray-100' : 'hover:bg-blue-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Autentificare
                </Link>
                <Link 
                  to="/register" 
                  className={`px-4 py-2 rounded-md font-medium text-center ${
                    isScrolled 
                      ? 'bg-blue-600 text-white hover:bg-blue-700' 
                      : 'bg-white text-blue-600 hover:bg-blue-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Înregistrare
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 
